/**
 * "A person dealt with this one", as a control, for both views of a call.
 *
 * THE AGENT'S GRADE AND A PERSON'S MARK ARE TWO FACTS, AND THIS FILE ONLY EVER WRITES THE
 * SECOND. `outcome.resolved` is what the agent said about its own call and is never
 * rewritten; `handled` is what happened after the caller hung up - somebody rang them back,
 * booked the car in, sorted it at the counter. A call can be unresolved by the agent and
 * handled by a person, and that is precisely the call this exists for: without it the
 * overview's "Needs your attention" only ever grows, and a number that only grows is a
 * number people stop reading.
 *
 * ONE COMPONENT, TWO SIZES, because the rule for WHEN to offer the button has to be the same
 * in the dialog and in the simplified register. Written twice it would drift, and the
 * register would offer to close a call the log says nobody needs to touch.
 *
 *   `normal`, in the call dialog: an open call gets the action and an optional line saying
 *   what was done; a call the agent dealt with still gets a quiet "Mark as handled",
 *   because a person may have followed up anyway and the log should be able to say so.
 *
 *   `large`, in the register on a counter tablet: one big button on an open call, and
 *   nothing at all on a call the agent handled. The prototype that asked for this view drew
 *   no actions on those rows, and a button a mechanic has no reason to press is a button
 *   they press by accident with a greasy thumb.
 *
 * THE ANSWER COMES BACK FROM THE WORKER AND IS SHOWN AT ONCE. `onChanged` hands it to the
 * caller so the row behind the dialog updates without a refetch, and this component shows
 * it in the meantime - the caller's own copy of the call arrives a render later, and the
 * button flicking back to its old state in between would read as the save having failed.
 */

import { useEffect, useState } from "react";
import { ApiError, post, type CallDetail, type CallSummary } from "../api";
import { useCurrentAgent } from "../currentAgent";
import { when } from "../format";
import { Button, TextInput, cx } from "../ui";
import { Check } from "./Icons";

/**
 * What a status pill says about a call. Three kinds and two words.
 *
 * `person` and `handled` both READ "Handled", and are kept apart because the register says
 * who: a call the agent dealt with needs nothing from anyone, and a call a person closed
 * carries that person's name.
 */
export interface CallStatus {
  kind: "open" | "handled" | "person";
  label: "Open" | "Handled";
}

export function callStatus(call: Pick<CallSummary, "needsPerson" | "handled">): CallStatus {
  if (call.handled) return { kind: "person", label: "Handled" };
  if (call.needsPerson) return { kind: "open", label: "Open" };
  return { kind: "handled", label: "Handled" };
}

/**
 * Why the agent handed this call to a person, in the words the overview's tile uses.
 *
 * FROM THE THREE COLUMNS THE WORKER'S PREDICATE READS, so the sentence and the flag cannot
 * disagree about the reason. Joined into one clause because an urgent, escalated call is
 * one call with two reasons rather than two calls.
 */
export function whyOpen(call: Pick<CallSummary, "outcome">): string {
  const reasons: string[] = [];
  if (call.outcome.priority === "urgent") reasons.push("marked urgent");
  if (call.outcome.handoff === "escalation") reasons.push("escalated to a person");
  if (call.outcome.resolved === false) reasons.push("not resolved on the call");
  if (!reasons.length) return "The agent flagged this call for a person.";
  const [first, ...rest] = reasons;
  const joined = rest.length
    ? `${[first, ...rest.slice(0, -1)].join(", ")} and ${rest.at(-1)}`
    : first!;
  return `${joined[0]!.toUpperCase()}${joined.slice(1)}.`;
}

/** A note long enough for "Rang back, booked in Thursday 9am", and short enough to stay one. */
const MAX_NOTE = 500;

export function HandledControl({
  call,
  size = "normal",
  onChanged,
}: {
  call: CallSummary;
  size?: "normal" | "large";
  /** The call as the Worker now has it, for the caller to put back in its list. */
  onChanged?: (call: CallDetail) => void;
}) {
  const { current } = useCurrentAgent();
  const tz = current?.timezone;
  const [fresh, setFresh] = useState<CallSummary | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A different call, or a fresh copy of this one from the caller: either way the prop is
  // now the truth, and what this component was holding is not.
  useEffect(() => {
    setFresh(null);
    setError(null);
    setNote("");
  }, [call]);

  const shown = fresh && fresh.id === call.id ? fresh : call;
  const large = size === "large";

  async function mark(handled: boolean) {
    setSaving(true);
    setError(null);
    try {
      const trimmed = note.trim();
      const { call: next } = await post<{ call: CallDetail }>(
        `/v1/calls/${call.id}/handled`,
        handled && trimmed ? { handled, note: trimmed } : { handled },
      );
      setFresh(next);
      setNote("");
      onChanged?.(next);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status !== 0
          ? err.message
          : "Couldn't save that. Check the connection and try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  const failure = error && (
    <p role="alert" className={cx("text-alert", large ? "text-lg" : "text-sm")}>
      {error}
    </p>
  );

  if (shown.handled) {
    const { at, by, note: done } = shown.handled;
    return (
      <div className={cx("grid", large ? "gap-3" : "gap-2")}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className={cx("flex items-center gap-2 text-ink", large ? "text-2xl" : "text-sm")}>
            <span
              aria-hidden="true"
              className={cx(
                "grid flex-none place-items-center rounded-full bg-good-soft text-good",
                large ? "size-9" : "size-5.5",
              )}
            >
              <Check size={large ? 20 : 13} />
            </span>
            <span>
              Handled{by ? <> by <strong className="font-semibold">{by}</strong></> : null}
              <span className="text-ink-soft">, {when(at, tz)}</span>
            </span>
          </p>
          <Button
            variant={large ? "default" : "ghost"}
            size={large ? "large" : "normal"}
            disabled={saving}
            onClick={() => void mark(false)}
          >
            {saving ? "Reopening…" : "Reopen"}
          </Button>
        </div>
        {done && (
          <p
            className={cx(
              "border-l-2 border-line pl-3 text-ink-soft",
              large ? "text-xl" : "text-sm",
            )}
          >
            {done}
          </p>
        )}
        {failure}
      </div>
    );
  }

  if (large) {
    // Nothing on a call the agent dealt with. See this file's header.
    if (!shown.needsPerson) return null;
    return (
      <div className="grid gap-3">
        <Button
          variant="primary"
          size="large"
          className="justify-self-start"
          disabled={saving}
          onClick={() => void mark(true)}
        >
          <Check size={22} />
          {saving ? "Saving…" : "I handled it myself"}
        </Button>
        {failure}
      </div>
    );
  }

  if (!shown.needsPerson) {
    return (
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink-soft">The agent dealt with this call.</p>
          <Button disabled={saving} onClick={() => void mark(true)}>
            {saving ? "Saving…" : "Mark as handled"}
          </Button>
        </div>
        {failure}
      </div>
    );
  }

  return (
    <form
      className="grid gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        void mark(true);
      }}
    >
      <p className="text-sm text-ink">{whyOpen(shown)}</p>
      <div className="flex flex-wrap items-center gap-2">
        <TextInput
          className="min-w-0 flex-1 basis-48"
          placeholder="What you did, optional"
          aria-label="What you did, optional"
          maxLength={MAX_NOTE}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? "Saving…" : "Mark as handled"}
        </Button>
      </div>
      {failure}
    </form>
  );
}
